import { useTheme } from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import React, { SVGProps, useId } from 'react';
import type { CustomSvgIconsProps, SvgPathProps } from './CustomSvgIcons.types';

const SvgPath = ({
  iconType,
  shapeId,
  solidColor,
}: SvgPathProps): React.ReactElement => {
  const iconTypeToStrokeWidth = {
    bold: '0.8',
    normal: '0.6',
    thin: '0.4',
  };

  const iconTypeToBorderWidth = {
    bold: '6',
    normal: '3',
    thin: '2',
  };

  const strokeWidth = iconTypeToStrokeWidth[iconType || 'normal'];
  const borderWidth = iconTypeToBorderWidth[iconType || 'normal'];

  const commonPathProps: SVGProps<SVGPathElement> = {
    clipRule: 'nonzero',
    fillOpacity: '1',
    fillRule: 'nonzero',
    strokeOpacity: 1,
    strokeWidth: strokeWidth,
    stroke: solidColor ? 'currentColor' : '',
  };

  return (
    <>
      <defs>
        <clipPath id={`${shapeId}_clip1`}>
          <path
            id={`${shapeId}_clip_1`}
            d="M 0.417969 0 L 25.582031 0 L 25.582031 25.160156 L 0.417969 25.160156 Z M 0.417969 0 "
            {...commonPathProps}
          />
        </clipPath>
        <clipPath id={`${shapeId}_clip2`}>
          <path
            id={`${shapeId}_clip_2`}
            d="M 13 0 C 6.054688 0 0.417969 5.632812 0.417969 12.582031 C 0.417969 19.527344 6.054688 25.160156 13 25.160156 C 19.945312 25.160156 25.582031 19.527344 25.582031 12.582031 C 25.582031 5.632812 19.945312 0 13 0 Z M 13 0 "
            {...commonPathProps}
          />
        </clipPath>
      </defs>
      <g id="surface1">
        <g clipPath={`url(#${shapeId}_clip1)`} clipRule="nonzero">
          <g clipPath={`url(#${shapeId}_clip2)`} clipRule="nonzero">
            <path
              id={`${shapeId}_1`}
              {...commonPathProps}
              d="M 20.000014 -0.00000266666 C 8.95874 -0.00000266666 -0.00220346 8.954731 -0.00220346 20.002215 C -0.00220346 31.043488 8.95874 39.998222 20.000014 39.998222 C 31.041287 39.998222 40.002231 31.043488 40.002231 20.002215 C 40.002231 8.954731 31.041287 -0.00000266666 20.000014 -0.00000266666 Z M 20.000014 -0.00000266666 "
              transform="matrix(0.629032,0,0,0.629032,0.419355,0.00000167742)"
              fill="none"
              strokeWidth={borderWidth}
              strokeLinecap="butt"
              strokeLinejoin="miter"
              strokeMiterlimit={4}
            />
          </g>
        </g>
        <path
          id={`${shapeId}_2`}
          {...commonPathProps}
          d="M 12.503906 15.097656 L 8.320312 15.097656 L 8.320312 14.542969 L 11.773438 9.652344 L 8.347656 9.652344 L 8.347656 9.097656 L 12.519531 9.097656 L 12.519531 9.625 L 9.050781 14.542969 L 12.503906 14.542969 Z M 12.503906 15.097656 "
        />
        <path
          id={`${shapeId}_3`}
          {...commonPathProps}
          d="M 14.171875 9.097656 L 14.171875 12.714844 L 17.070312 9.097656 L 17.789062 9.097656 L 15.757812 11.628906 L 17.828125 15.097656 L 17.125 15.097656 L 15.363281 12.132812 L 14.171875 13.605469 L 14.171875 15.097656 L 13.578125 15.097656 L 13.578125 9.097656 Z M 14.171875 9.097656 "
        />
      </g>
    </>
  );
};

export const ZeroKnowledgeProofIcon = ({
  shouldbegradient,
  transitionduration = 0.4,
  prefix,
  solidColor,
  iconType = 'normal',
  gradientColor,
  ...props
}: CustomSvgIconsProps): React.ReactElement => {
  const id = useId();
  const currColorId = `${prefix}_zkpColorGradient_${id}`;
  const newColorId = `${prefix}_zkpNewColorGradient_${id}`;
  const shapeId = `${prefix}_zkpShape_${id}`;

  const theme = useTheme();
  const isLight = theme.palette.type === 'light';
  const colorsDark = {
    base: {
      start: theme.palette?.greyColors?.PLUS_8,
      stop: theme.palette.greyColors?.PLUS_2,
    },
    hover: {
      start: theme.palette?.greyColors?.PLUS_8,
      stop: theme.palette.tealColors?.PLUS_2,
    },
  };
  const colorsLight = {
    base: {
      start: theme.palette?.greyColors?.PLUS_4,
      stop: theme.palette.greyColors?.PLUS_2,
    },
    hover: {
      start: theme.palette?.tealColors?.PLUS_3,
      stop: theme.palette.tealColors?.PLUS_2,
    },
  };
  const svgColors = isLight ? colorsLight : colorsDark;
  return (
    <SvgIcon
      {...props}
      width="40"
      zoomAndPan="magnify"
      viewBox="0 0 26 25.2"
      height="40"
      preserveAspectRatio="xMidYMid meet">
      {solidColor ? (
        <SvgPath
          shapeId={shapeId}
          iconType={iconType}
          solidColor={solidColor}
        />
      ) : (
        <>
          <defs>
            <linearGradient
              style={{
                transition: `${transitionduration}s`,
              }}
              id={currColorId}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%">
              <stop offset="0" stopColor={svgColors.base.start} />
              <stop offset="1" stopColor={svgColors.base.stop} />
            </linearGradient>
            <linearGradient id={newColorId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0" stopColor={svgColors.hover.start} />
              <stop offset="1" stopColor={svgColors.hover.stop} />
            </linearGradient>
          </defs>

          <g
            style={{
              stroke: `url("#${currColorId}")`,
            }}>
            <SvgPath
              shapeId={shapeId}
              iconType={iconType}
              solidColor={solidColor}
            />
          </g>
          <use
            xlinkHref={`#${shapeId}`}
            style={{
              stroke: `url("#${newColorId}")`,
              transition: `${transitionduration}s`,
              opacity: shouldbegradient ? 1 : 0,
            }}
          />
        </>
      )}
    </SvgIcon>
  );
};
